'use client';

import { useEffect, useState } from 'react';

interface NavigatorWithPerformanceHints extends Navigator {
    deviceMemory?: number;
    connection?: {
        saveData?: boolean;
        addEventListener?: (type: 'change', listener: () => void) => void;
        removeEventListener?: (type: 'change', listener: () => void) => void;
    };
}

function shouldUseLiteEffects() {
    const browserNavigator = navigator as NavigatorWithPerformanceHints;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hasLimitedCpu = browserNavigator.hardwareConcurrency > 0 && browserNavigator.hardwareConcurrency <= 4;
    const hasLimitedMemory = typeof browserNavigator.deviceMemory === 'number' && browserNavigator.deviceMemory <= 4;
    const savesData = browserNavigator.connection?.saveData === true;

    return prefersReducedMotion || hasLimitedCpu || hasLimitedMemory || savesData;
}

export default function useAdaptiveEffects() {
    const [useLiteEffects, setUseLiteEffects] = useState(false);

    useEffect(() => {
        const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
        const connection = (navigator as NavigatorWithPerformanceHints).connection;
        const updatePreference = () => setUseLiteEffects(shouldUseLiteEffects());

        updatePreference();
        motionPreference.addEventListener('change', updatePreference);
        connection?.addEventListener?.('change', updatePreference);

        return () => {
            motionPreference.removeEventListener('change', updatePreference);
            connection?.removeEventListener?.('change', updatePreference);
        };
    }, []);

    return useLiteEffects;
}
